/**
 * PerformanceMonitor
 *
 * Monitors AR session performance metrics including FPS, memory usage,
 * detection rates, and video playback statistics.
 */

export class PerformanceMonitor {
  /**
   * @param {Object} options - Configuration options
   * @param {number} options.sampleInterval - Sampling interval in ms (default: 1000)
   * @param {number} options.historySize - Number of samples to keep (default: 60)
   * @param {Function} options.onMetricsUpdate - Callback when metrics update (metrics) => void
   * @param {Function} options.onPerformanceDegradation - Callback when performance degrades (tier) => void
   */
  constructor(options = {}) {
    this.sampleInterval = options.sampleInterval || 1000; // 1 second
    this.historySize = options.historySize || 60; // 60 seconds of history
    this.onMetricsUpdate = options.onMetricsUpdate || null;
    this.onPerformanceDegradation = options.onPerformanceDegradation || null;

    // Metrics storage
    this.metrics = {
      fps: {
        current: 0,
        average: 0,
        min: Infinity,
        max: 0,
        history: [],
      },
      memory: {
        used: 0,
        total: 0,
        percentage: 0,
        history: [],
        // T034: Memory leak detection
        baseline: 0, // Baseline memory at session start
        delta: 0, // Current memory - baseline
        deltaPercentage: 0, // Delta as percentage of baseline
      },
      detection: {
        rate: 0, // Detection calls per second
        successRate: 0, // Percentage of successful detections
        totalDetections: 0,
        successfulDetections: 0,
        history: [],
      },
      video: {
        activeMarkerId: null,
        playbackState: 'stopped',
        bufferHealth: 100,
        switchCount: 0,
        lastSwitchAt: null,
      },
      markers: {
        tracking: 0,
        total: 0,
        states: new Map(), // markerId -> { status, duration }
      },
    };

    // Frame counting
    this.frameCount = 0;
    this.lastFrameTime = performance.now();
    this.fpsHistory = [];

    // Detection counting
    this.detectionCount = 0;
    this.successfulDetectionCount = 0;
    this.lastDetectionSampleTime = performance.now();

    // Monitoring state
    this.isMonitoring = false;
    this.monitoringInterval = null;
    this.startTime = null;

    // Degradation detection (T007)
    this.degradationThreshold = 24; // FPS threshold
    this.degradationDuration = 3000; // 3 seconds in ms
    this.degradationStartTime = null; // When degradation started
    this.isDegraded = false; // Current degradation state
    this.hasNotifiedDegradation = false; // Prevent duplicate notifications

    // T034: Memory leak detection thresholds
    this.memoryLeakThreshold = 0.05; // 5% increase threshold
    this.memoryLeakCheckDuration = 600000; // 10 minutes in ms
    this.hasWarnedMemoryLeak = false; // Prevent duplicate warnings
  }

  /**
   * Start monitoring
   */
  start() {
    if (this.isMonitoring) {
      console.warn('PerformanceMonitor already running');
      return;
    }

    console.log('📊 PerformanceMonitor started');
    this.isMonitoring = true;
    this.startTime = performance.now();
    this.lastFrameTime = performance.now();
    this.lastDetectionSampleTime = performance.now();

    // T034: Set memory baseline (Chrome only)
    if (performance.memory) {
      this.metrics.memory.baseline = Math.round(performance.memory.usedJSHeapSize / 1024 / 1024); // MB
      console.log(`📊 [T034] Memory baseline set: ${this.metrics.memory.baseline} MB`);
    }

    // Start sampling interval
    this.monitoringInterval = setInterval(() => {
      this._sampleMetrics();
    }, this.sampleInterval);
  }

  /**
   * Stop monitoring
   */
  stop() {
    if (!this.isMonitoring) {
      return;
    }

    console.log('📊 PerformanceMonitor stopped');
    this.isMonitoring = false;

    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }

  /**
   * Record a frame render (call this from render loop)
   */
  recordFrame() {
    if (!this.isMonitoring) return;

    this.frameCount++;

    const now = performance.now();
    const delta = now - this.lastFrameTime;

    if (delta >= 1000) {
      // Calculate FPS over last second
      const fps = Math.round((this.frameCount * 1000) / delta);
      this.metrics.fps.current = fps;
      this.metrics.fps.average = this._calculateAverage(this.metrics.fps.history);
      this.metrics.fps.min = Math.min(this.metrics.fps.min, fps);
      this.metrics.fps.max = Math.max(this.metrics.fps.max, fps);

      // Add to history
      this._addToHistory(this.metrics.fps.history, {
        timestamp: now,
        value: fps,
      });

      // Reset counters
      this.frameCount = 0;
      this.lastFrameTime = now;

      // T007: Check for performance degradation
      this._checkPerformanceDegradation(fps, now);
    }
  }

  /**
   * T006: Record frame rendering time
   * @param {number} duration - Frame rendering duration in milliseconds
   */
  recordFrameTime(duration) {
    if (!this.isMonitoring) return;

    // Store frame time in metrics (will be used for average calculation)
    if (!this.frameTimeHistory) {
      this.frameTimeHistory = [];
    }

    this.frameTimeHistory.push(duration);
    if (this.frameTimeHistory.length > 60) {
      this.frameTimeHistory.shift();
    }

    // Call recordFrame to update FPS
    this.recordFrame();
  }

  /**
   * T006: Record marker detection processing time
   * @param {number} duration - Detection duration in milliseconds
   */
  recordDetectionTime(duration) {
    if (!this.isMonitoring) return;

    if (!this.detectionTimeHistory) {
      this.detectionTimeHistory = [];
    }

    this.detectionTimeHistory.push(duration);
    if (this.detectionTimeHistory.length > 60) {
      this.detectionTimeHistory.shift();
    }
  }

  /**
   * T006: Record marker selection decision time
   * @param {number} duration - Selection duration in milliseconds
   */
  recordSelectionTime(duration) {
    if (!this.isMonitoring) return;

    if (!this.selectionTimeHistory) {
      this.selectionTimeHistory = [];
    }

    this.selectionTimeHistory.push(duration);
    if (this.selectionTimeHistory.length > 60) {
      this.selectionTimeHistory.shift();
    }
  }

  /**
   * Record a detection attempt
   * @param {boolean} success - Whether detection was successful
   * @param {number} trackingCount - Number of markers currently tracking
   */
  recordDetection(success, trackingCount = 0) {
    if (!this.isMonitoring) return;

    this.detectionCount++;
    if (success) {
      this.successfulDetectionCount++;
    }

    this.metrics.markers.tracking = trackingCount;
  }

  /**
   * Record video switch
   * @param {string} markerId - ID of marker being switched to
   */
  recordVideoSwitch(markerId) {
    if (!this.isMonitoring) return;

    this.metrics.video.switchCount++;
    this.metrics.video.lastSwitchAt = performance.now();
    this.metrics.video.activeMarkerId = markerId;
    this.metrics.video.playbackState = 'playing';

    console.log(`📊 Video switch recorded: ${markerId} (total: ${this.metrics.video.switchCount})`);
  }

  /**
   * Update marker states
   * @param {Array<Object>} markerStates - Array of MarkerState objects
   */
  updateMarkerStates(markerStates) {
    if (!this.isMonitoring) return;

    this.metrics.markers.total = markerStates.length;
    this.metrics.markers.tracking = markerStates.filter(
      (s) => s.trackingStatus === 'tracking'
    ).length;

    // Update individual marker tracking durations
    markerStates.forEach((state) => {
      const existing = this.metrics.markers.states.get(state.markerId);
      if (!existing) {
        this.metrics.markers.states.set(state.markerId, {
          status: state.trackingStatus,
          duration: 0,
          lastUpdate: performance.now(),
        });
      } else {
        const now = performance.now();
        const delta = now - existing.lastUpdate;

        if (state.trackingStatus === 'tracking') {
          existing.duration += delta;
        }

        existing.status = state.trackingStatus;
        existing.lastUpdate = now;
      }
    });
  }

  /**
   * Sample metrics (called at sample interval)
   * @private
   */
  _sampleMetrics() {
    const now = performance.now();

    // Calculate detection rate
    const detectionDelta = now - this.lastDetectionSampleTime;
    if (detectionDelta > 0) {
      const detectionRate = (this.detectionCount / detectionDelta) * 1000;
      const successRate =
        this.detectionCount > 0 ? (this.successfulDetectionCount / this.detectionCount) * 100 : 0;

      this.metrics.detection.rate = Math.round(detectionRate * 10) / 10;
      this.metrics.detection.successRate = Math.round(successRate * 10) / 10;
      this.metrics.detection.totalDetections += this.detectionCount;
      this.metrics.detection.successfulDetections += this.successfulDetectionCount;

      this._addToHistory(this.metrics.detection.history, {
        timestamp: now,
        rate: this.metrics.detection.rate,
        successRate: this.metrics.detection.successRate,
      });

      // Reset detection counters
      this.detectionCount = 0;
      this.successfulDetectionCount = 0;
      this.lastDetectionSampleTime = now;
    }

    // Sample memory (if available)
    if (performance.memory) {
      const used = performance.memory.usedJSHeapSize;
      const total = performance.memory.totalJSHeapSize;
      const percentage = (used / total) * 100;

      this.metrics.memory.used = Math.round(used / 1024 / 1024); // MB
      this.metrics.memory.total = Math.round(total / 1024 / 1024); // MB
      this.metrics.memory.percentage = Math.round(percentage * 10) / 10;

      // T034: Calculate memory delta from baseline
      if (this.metrics.memory.baseline > 0) {
        this.metrics.memory.delta = this.metrics.memory.used - this.metrics.memory.baseline;
        this.metrics.memory.deltaPercentage =
          (this.metrics.memory.delta / this.metrics.memory.baseline) * 100;

        // T034: Check for memory leak (>5% increase after 10 minutes)
        const sessionDuration = now - this.startTime;
        if (sessionDuration > this.memoryLeakCheckDuration && !this.hasWarnedMemoryLeak) {
          if (this.metrics.memory.deltaPercentage > this.memoryLeakThreshold * 100) {
            console.warn(
              `⚠️ [T034] Memory leak detected: +${this.metrics.memory.delta} MB (+${this.metrics.memory.deltaPercentage.toFixed(1)}%) after ${(sessionDuration / 60000).toFixed(1)} minutes`
            );
            this.hasWarnedMemoryLeak = true;

            // Optionally notify callback
            if (this.onPerformanceDegradation) {
              this.onPerformanceDegradation('memory-leak');
            }
          }
        }
      }

      this._addToHistory(this.metrics.memory.history, {
        timestamp: now,
        used: this.metrics.memory.used,
        percentage: this.metrics.memory.percentage,
        delta: this.metrics.memory.delta,
        deltaPercentage: this.metrics.memory.deltaPercentage,
      });
    }

    // Notify callback
    if (this.onMetricsUpdate) {
      this.onMetricsUpdate(this.getMetrics());
    }
  }

  /**
   * Add value to history with size limit
   * @param {Array} history - History array
   * @param {*} value - Value to add
   * @private
   */
  _addToHistory(history, value) {
    history.push(value);
    if (history.length > this.historySize) {
      history.shift();
    }
  }

  /**
   * Calculate average of history values
   * @param {Array<Object>} history - History array with {value} objects
   * @returns {number}
   * @private
   */
  _calculateAverage(history) {
    if (history.length === 0) return 0;
    const sum = history.reduce((acc, item) => acc + item.value, 0);
    return Math.round(sum / history.length);
  }

  /**
   * T007: Check for sustained performance degradation
   * @param {number} currentFps - Current FPS reading
   * @param {number} now - Current timestamp
   * @private
   */
  _checkPerformanceDegradation(currentFps, now) {
    // Check if FPS is below threshold
    if (currentFps < this.degradationThreshold && currentFps > 0) {
      // Start tracking degradation if not already tracking
      if (!this.degradationStartTime) {
        this.degradationStartTime = now;
        this.isDegraded = false;
        this.hasNotifiedDegradation = false;
      }

      // Check if degradation has persisted for required duration
      const degradationElapsed = now - this.degradationStartTime;
      if (degradationElapsed >= this.degradationDuration && !this.hasNotifiedDegradation) {
        this.isDegraded = true;
        this.hasNotifiedDegradation = true;

        // Notify callback if provided
        if (this.onPerformanceDegradation) {
          // Determine suggested quality tier based on FPS
          let suggestedTier = 'low';
          if (currentFps >= 20) suggestedTier = 'mid';
          else if (currentFps >= 15) suggestedTier = 'low';

          console.warn(
            `⚠️ Performance degradation detected: FPS ${currentFps} < ${this.degradationThreshold} for ${Math.round(degradationElapsed)}ms, suggesting tier: ${suggestedTier}`
          );
          this.onPerformanceDegradation(suggestedTier);
        }
      }
    } else {
      // FPS recovered, reset tracking
      if (this.degradationStartTime) {
        console.log(`✅ Performance recovered: FPS ${currentFps} >= ${this.degradationThreshold}`);
      }
      this.degradationStartTime = null;
      this.isDegraded = false;
      this.hasNotifiedDegradation = false;
    }
  }

  /**
   * Get current metrics snapshot
   * @returns {Object}
   */
  getMetrics() {
    // Calculate averages for new T006 metrics
    const avgFrameTime =
      this.frameTimeHistory && this.frameTimeHistory.length > 0
        ? this.frameTimeHistory.reduce((a, b) => a + b, 0) / this.frameTimeHistory.length
        : 0;

    const avgDetectionTime =
      this.detectionTimeHistory && this.detectionTimeHistory.length > 0
        ? this.detectionTimeHistory.reduce((a, b) => a + b, 0) / this.detectionTimeHistory.length
        : 0;

    const avgSelectionTime =
      this.selectionTimeHistory && this.selectionTimeHistory.length > 0
        ? this.selectionTimeHistory.reduce((a, b) => a + b, 0) / this.selectionTimeHistory.length
        : 0;

    return {
      timestamp: performance.now(),
      uptime: this.startTime ? performance.now() - this.startTime : 0,
      fps: {
        current: this.metrics.fps.current,
        average: this.metrics.fps.average,
        min: this.metrics.fps.min === Infinity ? 0 : this.metrics.fps.min,
        max: this.metrics.fps.max,
      },
      avgFrameTime: Math.round(avgFrameTime * 10) / 10,
      avgDetectionTime: Math.round(avgDetectionTime * 10) / 10,
      avgSelectionTime: Math.round(avgSelectionTime * 10) / 10,
      memory: {
        used: this.metrics.memory.used,
        total: this.metrics.memory.total,
        percentage: this.metrics.memory.percentage,
      },
      detection: {
        rate: this.metrics.detection.rate,
        successRate: this.metrics.detection.successRate,
        totalDetections: this.metrics.detection.totalDetections,
        successfulDetections: this.metrics.detection.successfulDetections,
      },
      video: {
        activeMarkerId: this.metrics.video.activeMarkerId,
        playbackState: this.metrics.video.playbackState,
        bufferHealth: this.metrics.video.bufferHealth,
        switchCount: this.metrics.video.switchCount,
        lastSwitchAt: this.metrics.video.lastSwitchAt,
      },
      markers: {
        tracking: this.metrics.markers.tracking,
        total: this.metrics.markers.total,
        trackingPercentage:
          this.metrics.markers.total > 0
            ? Math.round((this.metrics.markers.tracking / this.metrics.markers.total) * 100)
            : 0,
      },
    };
  }

  /**
   * Get metrics history
   * @returns {Object}
   */
  getHistory() {
    return {
      fps: [...this.metrics.fps.history],
      memory: [...this.metrics.memory.history],
      detection: [...this.metrics.detection.history],
    };
  }

  /**
   * Get per-marker statistics
   * @returns {Array<Object>}
   */
  getMarkerStats() {
    const stats = [];
    this.metrics.markers.states.forEach((state, markerId) => {
      stats.push({
        markerId,
        status: state.status,
        trackingDuration: Math.round(state.duration),
        trackingPercentage: this.startTime
          ? Math.round((state.duration / (performance.now() - this.startTime)) * 100)
          : 0,
      });
    });
    return stats;
  }

  /**
   * Get performance report
   * @returns {Object}
   */
  getReport() {
    const metrics = this.getMetrics();
    const markerStats = this.getMarkerStats();

    return {
      summary: {
        uptime: Math.round(metrics.uptime / 1000), // seconds
        avgFps: metrics.fps.average,
        minFps: metrics.fps.min,
        maxFps: metrics.fps.max,
        memoryUsed: metrics.memory.used,
        memoryPercentage: metrics.memory.percentage,
        detectionRate: metrics.detection.rate,
        detectionSuccessRate: metrics.detection.successRate,
        totalDetections: metrics.detection.totalDetections,
        videoSwitches: metrics.video.switchCount,
      },
      markers: markerStats,
      current: metrics,
    };
  }

  /**
   * T043: Export performance metrics in specified format
   * @param {string} format - Export format ('json' or 'csv')
   * @returns {string} Exported metrics as string
   */
  exportMetrics(format = 'json') {
    const report = this.getReport();
    const timestamp = new Date().toISOString();

    if (format === 'csv') {
      // CSV format: summary metrics + marker stats
      const lines = [];

      // Header
      lines.push('Metric,Value');

      // Summary metrics
      lines.push(`Timestamp,${timestamp}`);
      lines.push(`Uptime (s),${report.summary.uptime}`);
      lines.push(`Average FPS,${report.summary.avgFps}`);
      lines.push(`Min FPS,${report.summary.minFps}`);
      lines.push(`Max FPS,${report.summary.maxFps}`);
      lines.push(`Memory Used (MB),${report.summary.memoryUsed}`);
      lines.push(`Memory Percentage,${report.summary.memoryPercentage}`);
      lines.push(`Detection Rate (Hz),${report.summary.detectionRate}`);
      lines.push(`Detection Success Rate (%),${report.summary.detectionSuccessRate}`);
      lines.push(`Total Detections,${report.summary.totalDetections}`);
      lines.push(`Video Switches,${report.summary.videoSwitches}`);

      // Marker stats
      lines.push('');
      lines.push('Marker ID,Total Time (ms),Tracking Time (ms),Tracking %,Status');
      Object.entries(report.markers).forEach(([markerId, stats]) => {
        lines.push(
          `${markerId},${stats.totalTime},${stats.trackingTime},${stats.trackingPercentage},${stats.currentStatus}`
        );
      });

      return lines.join('\n');
    } else {
      // JSON format (default)
      return JSON.stringify(
        {
          exportedAt: timestamp,
          report,
          metrics: this.metrics,
        },
        null,
        2
      );
    }
  }

  /**
   * T043: Save exported metrics to file (triggers download in browser)
   * @param {string} format - Export format ('json' or 'csv')
   * @param {string} filename - Optional custom filename
   */
  downloadMetrics(format = 'json', filename = null) {
    const exportData = this.exportMetrics(format);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const defaultFilename = `performance-metrics-${timestamp}.${format}`;
    const finalFilename = filename || defaultFilename;

    // Create blob and trigger download
    const mimeType = format === 'csv' ? 'text/csv' : 'application/json';
    const blob = new Blob([exportData], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = finalFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`📊 [T043] Metrics exported to ${finalFilename} (${format.toUpperCase()})`);
  }

  /**
   * Reset all metrics
   */
  reset() {
    // Reset metrics
    this.metrics.fps.current = 0;
    this.metrics.fps.average = 0;
    this.metrics.fps.min = Infinity;
    this.metrics.fps.max = 0;
    this.metrics.fps.history = [];

    this.metrics.memory.history = [];
    this.metrics.detection.history = [];
    this.metrics.detection.totalDetections = 0;
    this.metrics.detection.successfulDetections = 0;

    this.metrics.video.switchCount = 0;
    this.metrics.video.lastSwitchAt = null;

    this.metrics.markers.states.clear();

    // Reset counters
    this.frameCount = 0;
    this.detectionCount = 0;
    this.successfulDetectionCount = 0;
    this.startTime = performance.now();
    this.lastFrameTime = performance.now();
    this.lastDetectionSampleTime = performance.now();

    // Reset degradation tracking (T007)
    this.degradationStartTime = null;
    this.isDegraded = false;
    this.hasNotifiedDegradation = false;

    console.log('📊 PerformanceMonitor reset');
  }

  /**
   * Check if performance is healthy
   * @param {Object} thresholds - Performance thresholds
   * @param {number} thresholds.minFps - Minimum acceptable FPS (default: 30)
   * @param {number} thresholds.maxMemoryPercent - Max memory percentage (default: 80)
   * @param {number} thresholds.minDetectionRate - Min detection rate (default: 10)
   * @returns {Object} Health status with warnings
   */
  checkHealth(thresholds = {}) {
    const defaults = {
      minFps: 30,
      maxMemoryPercent: 80,
      minDetectionRate: 10,
    };

    const t = { ...defaults, ...thresholds };
    const metrics = this.getMetrics();
    const warnings = [];
    let isHealthy = true;

    // Check FPS
    if (metrics.fps.current < t.minFps && metrics.fps.current > 0) {
      warnings.push({
        type: 'fps',
        severity: 'warning',
        message: `FPS below threshold: ${metrics.fps.current} < ${t.minFps}`,
      });
      isHealthy = false;
    }

    // Check memory
    if (metrics.memory.percentage > t.maxMemoryPercent) {
      warnings.push({
        type: 'memory',
        severity: 'warning',
        message: `Memory usage high: ${metrics.memory.percentage}% > ${t.maxMemoryPercent}%`,
      });
      isHealthy = false;
    }

    // Check detection rate
    if (metrics.detection.rate < t.minDetectionRate && this.isMonitoring) {
      warnings.push({
        type: 'detection',
        severity: 'info',
        message: `Detection rate low: ${metrics.detection.rate} < ${t.minDetectionRate} fps`,
      });
    }

    return {
      isHealthy,
      warnings,
      metrics: {
        fps: metrics.fps.current,
        memory: metrics.memory.percentage,
        detectionRate: metrics.detection.rate,
      },
    };
  }

  /**
   * Export metrics to JSON
   * @returns {string} JSON string
   */
  exportJSON() {
    return JSON.stringify(
      {
        report: this.getReport(),
        history: this.getHistory(),
        exportedAt: Date.now(),
      },
      null,
      2
    );
  }
}
